// The page an invitation or a reset link opens (slice 4.5): `/jelszo#<token>`.
// Public — the link is the credential — so it renders without a session and
// outside the shell. The token is read from the fragment, which the browser
// never sends to a server, and is posted in a request body; the API checks it
// (one use, an expiry, the purpose it was issued for) and rate-limits both
// doors. A good password signs the person straight in.

import { useEffect, useId, useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { checkPasswordLink, setPasswordWithLink } from "@/lib/api";
import type { LinkCheck } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Field } from "@/components/emc/Field";
import { ErrorState, LoadingState } from "@/components/emc/states";
import { minute } from "@/components/emc/format";
import { messageOf } from "@/pages/campaigns/model";
import { PASSWORD_MIN, passwordProblem, repeatProblem, tokenFromHash } from "@/pages/users/model";

type Stage = { kind: "checking" } | { kind: "refused"; message: string } | { kind: "ready"; link: LinkCheck };

const NO_LINK = "Ehhez a linkhez nem tartozik jelszó-beállítás. Nyisd meg újra a kapott linket, vagy kérj újat egy admintól.";

export default function SetPassword() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [token] = useState(() => tokenFromHash(window.location.hash));
  const [stage, setStage] = useState<Stage>({ kind: "checking" });
  const [password, setPassword] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const passwordId = useId();
  const againId = useId();

  useEffect(() => {
    if (token === "") {
      setStage({ kind: "refused", message: NO_LINK });
      return;
    }
    let cancelled = false;
    checkPasswordLink(token)
      .then((link) => !cancelled && setStage({ kind: "ready", link }))
      .catch((raised: unknown) => !cancelled && setStage({ kind: "refused", message: messageOf(raised) }));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const email = stage.kind === "ready" ? stage.link.email : "";
  const policy = password === "" ? null : passwordProblem(password, email);
  const mismatch = repeatProblem(password, again);
  const ready = password !== "" && again !== "" && policy === null && mismatch === null;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready || busy || stage.kind !== "ready") return;
    setBusy(true);
    setError(null);
    try {
      const done = await setPasswordWithLink(token, password);
      await login(done.email, password);
      // The fragment goes with the page: the spent link is not left in the
      // address bar or the history.
      navigate("/", { replace: true });
    } catch (raised: unknown) {
      setError(messageOf(raised));
      setBusy(false);
    }
  }

  return (
    <div className="grid min-h-screen place-items-center px-4 py-10">
      <Card className="w-full max-w-narrow gap-5 py-6">
        <CardHeader className="px-6">
          <h1>{stage.kind === "ready" && stage.link.purpose === "reset" ? "Új jelszó" : "Jelszó beállítása"}</h1>
          {stage.kind === "ready" && (
            <CardDescription>
              {stage.link.full_name ? `${stage.link.full_name}, ` : ""}
              {stage.link.purpose === "reset"
                ? "adj meg új jelszót ehhez a fiókhoz:"
                : "állíts be jelszót, és már be is léptél:"}{" "}
              <strong className="font-medium text-foreground">{stage.link.email}</strong>. A link{" "}
              <span className="data-mono">{minute(stage.link.expires_at)}</span>-ig érvényes.
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="px-6">
          {stage.kind === "checking" && <LoadingState label="A link ellenőrzése…" lines={2} />}
          {stage.kind === "refused" && (
            <div className="flex flex-col gap-4">
              <ErrorState message={stage.message} />
              <Button variant="outline" type="button" onClick={() => navigate("/", { replace: true })}>
                Belépés
              </Button>
            </div>
          )}
          {stage.kind === "ready" && (
            <form className="flex flex-col gap-4" onSubmit={(event) => void submit(event)}>
              <input type="email" autoComplete="username" value={stage.link.email} readOnly hidden />
              <Field
                label="Jelszó"
                htmlFor={passwordId}
                hint={policy ?? `Legalább ${PASSWORD_MIN} karakter, és nem lehet az email-címed.`}
              >
                <Input
                  id={passwordId}
                  type="password"
                  autoComplete="new-password"
                  required
                  aria-invalid={policy !== null || undefined}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoFocus
                />
              </Field>
              <Field label="Jelszó még egyszer" htmlFor={againId} hint={mismatch ?? undefined}>
                <Input
                  id={againId}
                  type="password"
                  autoComplete="new-password"
                  required
                  aria-invalid={mismatch !== null || undefined}
                  value={again}
                  onChange={(event) => setAgain(event.target.value)}
                />
              </Field>
              {error !== null && <ErrorState message={error} />}
              <Button className="mt-1 w-full" type="submit" disabled={busy || !ready}>
                {busy ? "Folyamatban…" : "Jelszó beállítása és belépés"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
